import type { AbilityHotkey } from '../../data/abilities.js';
import type { EntityId, PlayerId, Tick } from '../ids.js';

import type { CommandBase } from './Command.js';

// AbilityCommand fires a squad's hotkey-bound ability. The system looks up which
// ability the squad has from `ARCHETYPE_ABILITY` (Q) or
// `ARCHETYPE_SECONDARY_ABILITY` (E) in data/abilities.ts. Targeted abilities
// (Recon's Mark Target = Q on enemy squad; Recon's Cut Link = E on relay)
// require the matching `target.kind`.
//
// V1.5+ Slice 2 added `hotkey` to discriminate between Q and E so the right
// cooldown gets consumed. Default is 'Q' for backward compat with V1 callers
// that didn't set the field; the makeAbilityCommand factory writes 'Q'
// explicitly so the wire format never carries an undefined.
export type AbilityTargetRef =
  | { kind: 'squad'; id: EntityId }
  | { kind: 'core'; id: EntityId }
  | { kind: 'building'; id: EntityId }
  // V1.5+ Slice 2: Cut Link targets a control_node.
  | { kind: 'control_node'; id: EntityId };

export interface AbilityCommand extends CommandBase {
  kind: 'ability';
  squadId: EntityId;
  hotkey: AbilityHotkey;
  // Optional target. Mark Target requires a 'squad' target; Cut Link requires a
  // 'control_node' target; the squad-anchored abilities ignore it.
  target: AbilityTargetRef | null;
}

export function makeAbilityCommand(args: {
  issuedBy: PlayerId;
  tick: Tick;
  squadId: EntityId;
  hotkey?: AbilityHotkey;
  target?: AbilityTargetRef | null;
}): AbilityCommand {
  return {
    kind: 'ability',
    issuedBy: args.issuedBy,
    tick: args.tick,
    squadId: args.squadId,
    hotkey: args.hotkey ?? 'Q',
    target: args.target ?? null,
  };
}
